class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Atelier: Shop Pay, View bag and Checkout wait for every tapped quantity and the note (cart.js CartQueue.settle).
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    const warm = () => this.querySelector('at-suggest')?.warmImages?.();
    cartLink.addEventListener('pointerenter', warm, { once: true });
    cartLink.addEventListener('focus', warm, { once: true });
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // Atelier: the drawer's stylesheets load without blocking the page (at-cart-drawer.css in snippets/cart-drawer.liquid,
  // the deferred component sheets in layout/theme.liquid, all marked data-at-drawer-css). An Add on a slow connection
  // can arrive first; the bag then opens the moment they have all applied, never unstyled or unpositioned.
  // A sheet that failed (its onerror marks data-failed, before or after the bag was asked to open) is not waited for
  // again, and the bag page, which needs none of these sheets, opens instead of an unstyled drawer.
  static pendingSheets() {
    return [...document.querySelectorAll('link[data-at-drawer-css]')].filter(
      (link) => !link.dataset.loaded && !link.dataset.failed && link.media !== 'all'
    );
  }

  static whenStyled() {
    return Promise.all(
      CartDrawer.pendingSheets().map(
        (link) =>
          new Promise((resolve) => {
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener(
              'error',
              () => {
                link.dataset.failed = 1;
                resolve();
              },
              { once: true }
            );
          })
      )
    );
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (document.querySelector('link[data-at-drawer-css][data-failed]')) {
      window.location.assign(window.routes.cart_url);
      return;
    }
    this.querySelector('at-suggest')?.warmImages?.();
    if (CartDrawer.pendingSheets().length) {
      return (this.opening = CartDrawer.whenStyled().then(() => this.open(triggeredBy)));
    }
    CartQueue.checkFresh();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Atelier: ask Rebuy for the suggestions row's picks now that the shopper is looking at it (assets/at-cart.js;
    // a re-render while open is handled by <at-suggest> itself). Nothing is fetched on page views that never open it.
    this.querySelector('at-suggest')?.refresh?.();
    const trap = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    // Atelier: with Reduce Motion the drawer appears without sliding (at-cart-drawer.css), so no transitionend comes;
    // focus moves in straight away instead.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) this.addEventListener('transitionend', trap, { once: true });
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    const opened = new Promise((resolve) => {
      setTimeout(() => {
        this.classList.add('animate', 'active');
        if (reduced) trap();
        resolve();
      });
    });

    document.body.classList.add('overflow-hidden');
    // Atelier: the page behind the open bag is inert (global.js)
    inertPage(this, { isOpen: () => this.classList.contains('active'), close: () => this.close() });

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    return (this.opening = opened);
  }

  close() {
    this.classList.remove('active');
    this.clearLiveRegion();
    // Atelier: the stored trigger can be gone (an in-bag suggestion's Add button is destroyed by the re-render);
    // return focus to the bag icon rather than dropping it on <body>.
    const returnTo =
      this.activeElement && document.contains(this.activeElement)
        ? this.activeElement
        : document.getElementById('cart-icon-bubble');
    releasePage(this); // before focus goes back: an inert element cannot take it
    removeTrapFocus(returnTo);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Atelier: stayClosed is true when the drawer was opened for this add with its pending line (product-form.js
  // showPending) and she closed it before the answer came: the bag is painted but not opened again.
  renderContents(parsedState, change, stayClosed = false) {
    // Atelier: the add succeeded but its answer can lack the rendered sections (see CartItems.withSections in cart.js).
    // Fetch them and render as usual. If that fails too: an Add from inside the open drawer (a suggestion) refreshes
    // every view through CartQueue; an Add from the page shows the bag page, which always reflects the add, rather than
    // leaving the drawer shut with a TypeError.
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    if (!parsedState.sections || sectionIds.some((id) => !parsedState.sections[id])) {
      return CartItems.withSections(parsedState.sections, sectionIds)
        .then((sections) => this.renderContents({ ...parsedState, sections }, change, stayClosed))
        .catch(() => {
          if (this.classList.contains('active')) {
            return CartQueue.refresh(true);
          }
          // Leave once product-form's /cart.json read for Shopify's cart:lines-update event has settled (it never
          // rejects): navigating first aborted it, so the add's analytics event failed with "Missing currency".
          return (CartItems.pendingCartDataPromise || Promise.resolve()).then(() => {
            window.location = window.routes.cart_url;
          });
        });
    }
    // Atelier: true for an Add from the in-bag suggestions (snippets/at-cart-upsell.liquid); open() returns early then.
    const wasOpen = this.classList.contains('active');
    // Atelier: `change` is the Add's number (product-form.js, CartItems.startChange): an answer older than a change that
    // has already painted does not paint. The store may still have applied this add after that change (a + sent later
    // and answered first), so the bag is fetched afresh (CartQueue.refresh) rather than left without this dress.
    const paints = !change || CartItems.mayPaint(change);
    // Returned so product-form keeps the pending line (and Checkout's hold) until the fresh bag has painted.
    const refreshed = paints ? null : CartQueue.refresh(true);
    if (paints) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      const restore = CartItems.holdDrawer(this);
      this.getSectionsToRender().forEach((section) => {
        if (section.page) return;
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        CartItems.replaceContents(sectionElement, this.getSectionDOM(parsedState.sections[section.id], section.selector));
      });
      restore();
      // Atelier: on /cart, the page's lines, subtotal and Checkout from the same answer (an Add over an empty /cart left
      // it "empty" with Checkout hidden, and a filled one kept the old subtotal).
      const page = document.querySelector('cart-items');
      if (page) CartItems.paint(parsedState.sections, page.getSectionsToRender().filter((s) => /^main-cart-/.test(s.id)));
    }

    if (wasOpen) this.reseatFocus();

    return Promise.resolve(refreshed).then(async () => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!stayClosed) await this.open();
      // Quantity edits keep their place; an Add must reveal the size she just chose, even after a long bag was scrolled.
      this.revealAddedLine(parsedState);
      // Next task, so the re-rendered status region is already in the accessibility tree when its text changes.
      if (wasOpen) setTimeout(() => this.announceAdded(parsedState));
    });
  }

  revealAddedLine(item) {
    const box = [...this.querySelectorAll('.quantity__input')].find((input) =>
      item.key ? input.dataset.quantityLineKey === item.key : input.dataset.quantityVariantId === String(item.id)
    );
    const row = box?.closest('.cart-item');
    const items = this.querySelector('cart-drawer-items');
    const list = items && getComputedStyle(items).overflowY === 'visible' ? this.querySelector('.drawer__inner') : items;
    if (!row || !list) return;
    const line = row.getBoundingClientRect();
    const visible = list.getBoundingClientRect();
    if (line.top < visible.top || line.bottom > visible.bottom) list.scrollTop += line.top - visible.top;
  }

  // Atelier: after an in-drawer Add, renderContents above re-renders the open drawer (and, if its sections were
  // missing, CartDrawerItems.onCartUpdate replaces <cart-drawer-items> and the footer). That destroys the focused Add
  // button, and open()'s trapFocus only runs on transitionend of a fresh open, so focus fell to <body>: Escape did
  // nothing, nothing was announced and Tab wrapped against detached nodes. Keep focus where it is if it survived,
  // otherwise put it on the dialog as open() does, and re-arm the trap on the new nodes.
  reseatFocus() {
    if (!this.classList.contains('active')) return;
    const current = document.activeElement;
    const target =
      current && current !== document.body && this.contains(current)
        ? current
        : this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (target) trapFocus(document.getElementById('CartDrawer'), target);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      // Atelier: on /cart an Add answers with the page's lines and footer too (painted by CartItems.paint above).
      ...(document.querySelector('cart-items')?.getSectionsToRender() || [])
        .filter((section) => /^main-cart-/.test(section.id))
        .map((section) => ({ id: section.section, page: true })),
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  // Atelier: read by close() for the return-focus target. product-form.js stores document.activeElement before every
  // add, so an in-bag suggestion's Add would replace the real trigger (bag icon or the page's Add to bag button) with a
  // node that the re-render destroys. Anything inside the drawer is ignored while it is open.
  setActiveElement(element) {
    if (element && this.classList.contains('active') && this.contains(element)) return;
    this.activeElement = element;
  }

  // Atelier: "Added <title> to your bag." in the drawer's status region after an in-drawer add (the add response is the
  // line item, so product_title is the product). No timer (house rule): the region is emptied at the start of the next
  // announcement and when the drawer closes (clearLiveRegion), so a stale message is not left for the virtual cursor
  // to find; renderContents has already replaced the region with a fresh empty node, so the new text is always a change.
  announceAdded(parsedState) {
    const status = this.querySelector('#CartDrawer-LiveRegionText');
    if (!status) return;
    this.clearLiveRegion();
    const title = parsedState && (parsedState.product_title || parsedState.title);
    // Words from the locale (snippets/cart-drawer.liquid data-added / data-added-no-title); English fallbacks.
    const withTitle = this.dataset.added || 'Added [title] to your bag.';
    status.textContent = title ? withTitle.split('[title]').join(title) : this.dataset.addedNoTitle || 'Added to your bag.';
  }

  clearLiveRegion() {
    const status = this.querySelector('#CartDrawer-LiveRegionText');
    if (status && status.textContent) status.textContent = '';
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Atelier: see CartDrawer.reseatFocus. The parent replaces this element after a fetch; when focus was inside the open
  // drawer as the update started (an in-bag suggestion's Add), put it back once the new nodes are in.
  onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    const focusWasInside = !!drawer && drawer.classList.contains('active') && drawer.contains(document.activeElement);
    const pending = super.onCartUpdate();
    if (focusWasInside && pending && typeof pending.then === 'function') {
      pending.then(() => document.querySelector('cart-drawer')?.reseatFocus());
    }
    return pending;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
